import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { ChevronLeft, ChevronRight, Plus, Search, X } from "lucide-react";
import { toast } from "sonner";
import { useAppSelector } from "hooks";
import { canManageOnboarding } from "utils/onboardingPermissions";
import { createPosition } from "services/positions.service";
import { usePositionsCatalogue } from "./usePositionsCatalogue";
import {
  filterPositions,
  genderedTitle,
  movePositionId,
  positionName,
  togglePositionId,
} from "./positions-helpers";
import { describePositionsError } from "./positions-errors";

/**
 * Picks an employee's positions from the company catalogue — the free-text
 * position inputs this replaces are gone for good (positions catalogue §1
 * rule 2). The value is an ORDERED id list: the chips show that order, it can
 * be changed with the arrows, and the contract prints the names in it.
 *
 * With catalogue rights (Master / Access onboarding) a missing position can be
 * created inline, LT + EN, and is selected at once.
 */

type Props = {
  companyId: string;
  value: string[];
  onChange: (ids: string[]) => void;
  label?: string;
  required?: boolean;
  /** Already translated message shown under the field. */
  error?: string;
  disabled?: boolean;
  /** Overrides the store-derived catalogue right (tests, read-only callers). */
  canCreate?: boolean;
};

export default function PositionMultiSelect({
  companyId,
  value,
  onChange,
  label,
  required,
  error,
  disabled = false,
  canCreate,
}: Props) {
  const { t, i18n } = useTranslation();
  const lang = (i18n as any).language === "en" ? "en" : "lt";
  const role = useAppSelector((state) => state.authReducer.role);
  const mayCreate = canCreate ?? canManageOnboarding(role);
  const catalogue = usePositionsCatalogue(companyId);
  const [query, setQuery] = useState("");
  const [creating, setCreating] = useState(false);
  const [newLT, setNewLT] = useState("");
  const [newLTFemale, setNewLTFemale] = useState("");
  const [newEN, setNewEN] = useState("");
  const [saving, setSaving] = useState(false);

  const visible = useMemo(
    () => filterPositions(catalogue.active, query),
    [catalogue.active, query]
  );

  const openCreate = () => {
    setNewLT(query.trim());
    setNewLTFemale("");
    setNewEN("");
    setCreating(true);
  };

  const submitCreate = async () => {
    if (!newLT.trim()) {
      toast.error(t("positions_name_lt_required"));
      return;
    }
    setSaving(true);
    try {
      const created = await createPosition(companyId, {
        nameLT: newLT.trim(),
        nameLTFemale: newLTFemale.trim() || null,
        nameEN: newEN.trim() || null,
      });
      catalogue.add(created);
      if (created.id && !value.includes(created.id)) onChange([...value, created.id]);
      setCreating(false);
      setQuery("");
    } catch (e: any) {
      toast.error(describePositionsError(e, t));
    } finally {
      setSaving(false);
    }
  };

  const fieldLabel = label ?? t("positions_title");

  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between gap-2">
        <span className="block text-sm font-medium text-gray-700">
          {fieldLabel}
          {required && <span className="ml-0.5 text-red-500">*</span>}
        </span>
        {value.length > 1 && !disabled && (
          <span className="text-xs text-gray-500">{t("positions_order_hint")}</span>
        )}
      </div>

      {/* Chosen positions, in contract order */}
      <div
        className={clsx(
          "flex min-h-[2.5rem] flex-wrap items-center gap-1.5 rounded-lg border px-2 py-1.5",
          error ? "border-red-500" : "border-gray-300",
          disabled && "bg-gray-50"
        )}
        aria-label={fieldLabel}
      >
        {value.length === 0 && (
          <span className="px-1 text-sm text-gray-400">{t("positions_none_selected")}</span>
        )}
        {value.map((id, index) => {
          const position = catalogue.byId.get(id);
          const name = position
            ? positionName(position, lang)
            : catalogue.loading
              ? "…"
              : t("positions_unknown");
          return (
            <span
              key={id}
              // A phone gets one chosen position per row, with finger-sized arrow and remove buttons.
              className="inline-flex items-center gap-0.5 rounded-md bg-blue-50 py-0.5 pl-2 pr-1 text-sm text-blue-900 ring-1 ring-inset ring-blue-200 max-md:w-full max-md:py-0"
            >
              {!disabled && value.length > 1 && (
                <button
                  type="button"
                  className="rounded p-0.5 text-blue-500 hover:bg-blue-100 disabled:opacity-30 max-md:p-3"
                  onClick={() => onChange(movePositionId(value, index, -1))}
                  disabled={index === 0}
                  aria-label={t("positions_move_left")}
                  title={t("positions_move_left")}
                >
                  <ChevronLeft className="h-3.5 w-3.5 max-md:h-5 max-md:w-5" />
                </button>
              )}
              <span className="px-0.5 max-md:min-w-0 max-md:flex-1 max-md:break-words">{name}</span>
              {position?.isArchived && (
                <span className="text-xs text-amber-700">({t("positions_archived_badge")})</span>
              )}
              {!disabled && value.length > 1 && (
                <button
                  type="button"
                  className="rounded p-0.5 text-blue-500 hover:bg-blue-100 disabled:opacity-30 max-md:p-3"
                  onClick={() => onChange(movePositionId(value, index, 1))}
                  disabled={index === value.length - 1}
                  aria-label={t("positions_move_right")}
                  title={t("positions_move_right")}
                >
                  <ChevronRight className="h-3.5 w-3.5 max-md:h-5 max-md:w-5" />
                </button>
              )}
              {!disabled && (
                <button
                  type="button"
                  className="rounded p-0.5 text-blue-500 hover:bg-blue-100 max-md:p-3"
                  onClick={() => onChange(value.filter((v) => v !== id))}
                  aria-label={t("remove")}
                  title={t("remove")}
                >
                  <X className="h-3.5 w-3.5 max-md:h-5 max-md:w-5" />
                </button>
              )}
            </span>
          );
        })}
      </div>
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}

      {!disabled && (
        <>
          <label className="relative mt-2 block">
            <Search aria-hidden className="pointer-events-none absolute left-2 top-3 h-4 w-4 text-gray-400 md:top-2.5" />
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t("positions_picker_search")}
              aria-label={t("positions_picker_search")}
              className="w-full rounded-md border border-gray-300 py-2 pl-8 pr-2 text-base focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 md:text-sm"
            />
          </label>
          <div
            role="group"
            aria-label={t("positions_picker_search")}
            className="mt-2 max-h-48 divide-y divide-gray-100 overflow-y-auto rounded-md border border-gray-200"
          >
            {catalogue.loading ? (
              <div className="px-3 py-2 text-sm text-gray-500">{t("loading")}</div>
            ) : catalogue.error ? (
              <div className="flex items-center justify-between gap-2 px-3 py-2 text-sm text-red-700">
                <span>{t("positions_load_failed")}</span>
                <button type="button" className="underline" onClick={catalogue.reload}>
                  {t("retry")}
                </button>
              </div>
            ) : visible.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500">{t("positions_picker_empty")}</div>
            ) : (
              visible.map((position) => (
                <label
                  key={position.id}
                  className="flex cursor-pointer items-start gap-2 px-3 py-2 text-sm hover:bg-gray-50 max-md:py-3"
                >
                  <input
                    type="checkbox"
                    checked={value.includes(position.id)}
                    onChange={() => onChange(togglePositionId(value, position.id))}
                    className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500 max-md:h-5 max-md:w-5"
                  />
                  <span className="min-w-0">
                    <span className="block truncate text-gray-900">{genderedTitle(position)}</span>
                    {position.nameEN && (
                      <span className="block truncate text-xs text-gray-500">{position.nameEN}</span>
                    )}
                  </span>
                </label>
              ))
            )}
          </div>

          {mayCreate && !creating && (
            <button
              type="button"
              className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-800 max-md:min-h-11"
              onClick={openCreate}
            >
              <Plus className="h-4 w-4" aria-hidden />
              {t("positions_picker_create")}
            </button>
          )}
          {mayCreate && creating && (
            <div className="mt-2 rounded-md border border-gray-200 bg-gray-50 p-3">
              <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                <label className="block text-sm">
                  <span className="mb-1 block font-medium text-gray-700">
                    {t("positions_name_lt")} <span className="text-red-500">*</span>
                  </span>
                  <input
                    type="text"
                    value={newLT}
                    onChange={(event) => setNewLT(event.target.value)}
                    maxLength={255}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-base focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 md:text-sm"
                  />
                </label>
                <label className="block text-sm">
                  <span className="mb-1 block font-medium text-gray-700">{t("positions_name_lt_female")}</span>
                  <input
                    type="text"
                    value={newLTFemale}
                    onChange={(event) => setNewLTFemale(event.target.value)}
                    maxLength={255}
                    placeholder={t("positions_name_lt_female_placeholder")}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-base focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 md:text-sm"
                  />
                </label>
                <label className="block text-sm">
                  <span className="mb-1 block font-medium text-gray-700">{t("positions_name_en")}</span>
                  <input
                    type="text"
                    value={newEN}
                    onChange={(event) => setNewEN(event.target.value)}
                    maxLength={255}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-base focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 md:text-sm"
                  />
                </label>
              </div>
              <div className="mt-2 flex justify-end gap-2">
                <button
                  type="button"
                  className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 max-md:min-h-11"
                  onClick={() => setCreating(false)}
                  disabled={saving}
                >
                  {t("cancel")}
                </button>
                <button
                  type="button"
                  className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 max-md:min-h-11"
                  onClick={submitCreate}
                  disabled={saving}
                >
                  {t("positions_create")}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
